/**
 * A SHOP PAGE IS BUILT ONCE, IN THE LANGUAGE IT WILL STAY IN.
 *
 * `App.js` keys its root on the language, so a language change rebuilds every
 * page on screen -- and a page reports what it reports ON MOUNT again. The
 * first visit to a shop is exactly when its language is not yet known: the
 * shop's record arrives after the page has mounted, the language turns (see
 * utils/shopLanguage), the product page is rebuilt, and a catalogue ad's click
 * sent Meta two ViewContents under two event ids, two server copies of that
 * step, and two `product_open`s.
 *
 * So the language is settled BEFORE a shop page is rendered:
 *
 *   - the shop's languages are known (remembered from an earlier page) and the
 *     page is already in the right language: the page renders, no wait;
 *   - known, and the language must turn: a neutral placeholder renders, the
 *     language turns (the app's rebuild then rebuilds only the placeholder), and
 *     the page is built once, in the new language;
 *   - not known (the first page of a shop in this browser): the placeholder
 *     renders while the shop is asked for them, then as above. The ask is the
 *     same getSalonBySlug/getSalonByHost every shop page makes.
 *
 * It never blanks a page for long and never rebuilds one that is showing:
 *   - an ask that fails or takes longer than SHOP_LANGUAGE_WAIT_MS lets the page
 *     render in the language it is in; its answer, when it comes, is remembered
 *     for the next page -- this one is not turned;
 *   - the page on screen (its history entry) is never held again, so a language
 *     the reader picks on it, or an answer arriving late, does not unmount it.
 *
 * Pages that are not a shop's own render straight through, exactly as before.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import i18n from 'i18next';
import { getSalonByHost, getSalonBySlug } from '../../api/storeApi';
import { getDomainHost, isDomainMode } from '../../utils/domainMode';
import {
  hasRememberedShopLanguages,
  isShopLanguagePath,
  rememberShopLanguages,
  rememberedShopLanguages,
  shopKeyOfPath,
  shopLanguagesOf,
  shopUiLanguage,
} from '../../utils/shopLanguage';

/** The longest a shop's first page waits to learn its language before it renders anyway. */
export const SHOP_LANGUAGE_WAIT_MS = 1500;

/** Asks in flight, one per shop. */
const asking = new Map();
/** Shops whose ask failed or ran out of time on this page load: their pages no longer wait. */
const givenUp = new Set();
/** The history entry whose page is on screen. */
let shownEntry = null;

const codeOf = (value) => String(value || '').split('-')[0];

function askShop(key, domainMode) {
  if (!asking.has(key)) {
    const ask = Promise.resolve()
      .then(() => (domainMode ? getSalonByHost(key) : getSalonBySlug(key)))
      .then((data) => { rememberShopLanguages(key, shopLanguagesOf(data?.salon ?? data)); })
      .catch(() => { givenUp.add(key); })
      .finally(() => { asking.delete(key); });
    asking.set(key, ask);
  }
  return asking.get(key);
}

export default function ShopLanguageGate({ children }) {
  const location = useLocation();
  const [, rerender] = useState(0);
  const domainMode = isDomainMode();
  const onShop = isShopLanguagePath(location.pathname, domainMode);
  const key = onShop ? shopKeyOfPath(location.pathname, domainMode, getDomainHost()) : '';
  const entry = `${location.key}|${location.pathname}`;

  let wait = null; // null: render the page | 'ask' | a language to turn to
  if (onShop && shownEntry !== entry) {
    if (hasRememberedShopLanguages(key)) {
      const target = shopUiLanguage(rememberedShopLanguages(key));
      if (target && codeOf(i18n.language) !== target && !givenUp.has(`turn:${key}:${target}`)) wait = target;
    } else if (!givenUp.has(key)) {
      wait = 'ask';
    }
  }

  useEffect(() => {
    if (!wait) return undefined;
    let live = true;
    const again = () => { if (live) rerender((n) => n + 1); };
    if (wait === 'ask') {
      const timer = setTimeout(() => { givenUp.add(key); again(); }, SHOP_LANGUAGE_WAIT_MS);
      askShop(key, domainMode).finally(() => { clearTimeout(timer); again(); });
      return () => { live = false; clearTimeout(timer); };
    }
    // Turning the language rebuilds the app (App.js), this placeholder included, and the
    // rebuilt gate renders the page. Without a rebuild this gate renders it itself; should the
    // language somehow not turn, the page is shown in the language it is in.
    Promise.resolve(i18n.changeLanguage(wait)).catch(() => {}).finally(() => {
      if (codeOf(i18n.language) !== wait) givenUp.add(`turn:${key}:${wait}`);
      again();
    });
    return () => { live = false; };
  }, [wait, key, domainMode]);

  if (wait) {
    return <div data-testid="shop-language-pending" aria-busy="true" style={{ minHeight: '100vh' }} />;
  }
  shownEntry = entry;
  return children;
}

/** Tests only: forget what this page load has learned. */
export function __resetShopLanguageGate() {
  asking.clear();
  givenUp.clear();
  shownEntry = null;
}
